import { useEffect, useRef } from 'react';
import { StyledModal } from '@app/components/modal/Modal';
import {
    type ModalProps,
    type TableColumnsType,
    // , Checkbox
} from 'antd';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { HiOutlineTrash } from 'react-icons/hi2';
import { MdCheck, MdAdd } from 'react-icons/md';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@app/components/form-fields/TextField';
import { StyledTable } from '@app/components/table/Table';
import { StyledButton } from '@app/components/button/Button';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import type { ContextVariable, ContextVariablesFormData } from '@app/services/aiFlow.types';

const DEFAULT_VARIABLE = {
    name: '',
    description: '',
    required: false,
    type: 'string',
    defaultValue: '',
};

type FieldVariable = ContextVariable & { id: string };

type FlowContextModalProps = ModalProps & {
    onSubmit: (data: { variables: ContextVariablesFormData }) => void;
    onCancel: () => void;
    data?: {
        contextVariables: ContextVariable[];
        setContextVariables: React.Dispatch<React.SetStateAction<ContextVariable[]>>;
    };
};

const schema = z.object({
    variables: z.array(
        z.object({
            name: z
                .string()
                .trim()
                .min(1, 'Name is required')
                .regex(/^[^\s]+$/, 'Name cannot contain spaces'),
            description: z.string().trim(),
            required: z.boolean(),
            defaultValue: z.string().trim(),
        })
    ),
});

const FlowContextModal = ({ data, onCancel, onSubmit, ...props }: FlowContextModalProps) => {
    const formRef = useRef<HTMLFormElement>(null);
    const {
        handleSubmit,
        control,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'onSubmit',
        resolver: zodResolver(schema),
        defaultValues: { variables: data?.contextVariables },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'variables',
    });

    useEffect(() => {
        if (data?.contextVariables) {
            reset({ variables: data.contextVariables });
        }
    }, [data?.contextVariables, reset]);

    const columns: TableColumnsType<FieldVariable> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 270,
            render: (_, __, idx) => (
                <Controller
                    control={control}
                    name={`variables.${idx}.name`}
                    render={({ field, fieldState }) => (
                        <div className="name-field">
                            <span>{'{{'}</span>
                            <TextField
                                fieldState={fieldState}
                                {...field}
                                type="text"
                                placeholder="Enter variable name"
                            />
                            <span>{'}}'}</span>
                        </div>
                    )}
                />
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (_, __, idx) => (
                <Controller
                    control={control}
                    name={`variables.${idx}.description`}
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            type="text"
                            placeholder="Enter description"
                        />
                    )}
                />
            ),
        },
        // {
        //     title: 'Required',
        //     dataIndex: 'required',
        //     key: 'required',
        //     align: 'center',
        //     width: 100,
        //     render: (_, __, idx) => (
        //         <Controller
        //             control={control}
        //             name={`variables.${idx}.required`}
        //             render={({ field }) => (
        //                 <Checkbox
        //                     {...field}
        //                     checked={field.value}
        //                     onChange={(e) => field.onChange(e.target.checked)}
        //                 />
        //             )}
        //         />
        //     ),
        // },
        {
            title: 'Default Value',
            dataIndex: 'defaultValue',
            key: 'defaultValue',
            width: 240,
            render: (_, __, idx) => (
                <Controller
                    control={control}
                    name={`variables.${idx}.defaultValue`}
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            type="text"
                            placeholder="Enter default (optional)"
                        />
                    )}
                />
            ),
        },
        {
            dataIndex: 'actions',
            key: 'actions',
            width: 125,
            render: (_, __, idx) => (
                <StyledButton icon={<HiOutlineTrash />} size="small" onClick={() => remove(idx)}>
                    Delete
                </StyledButton>
            ),
        },
    ];

    return (
        <StyledModal
            rootClassName="context-variables-modal"
            title={'Context Variables'}
            mask={{ closable: false }}
            footer={
                <>
                    <StyledButton
                        icon={<MdAdd size={20} />}
                        htmlType="button"
                        color="blue"
                        type="primary"
                        onClick={() => append(DEFAULT_VARIABLE)}>
                        Add New
                    </StyledButton>
                    <StyledButtonGroup justify="space-between">
                        <StyledButton
                            onClick={onCancel}
                            color="black"
                            data-testid="reset-button"
                            htmlType="button">
                            Cancel
                        </StyledButton>
                        <StyledButton
                            htmlType="button"
                            onClick={() => formRef.current?.requestSubmit()}
                            type="primary"
                            disabled={!isDirty || !isValid || isSubmitting}
                            loading={isSubmitting || props?.loading}
                            icon={<MdCheck />}>
                            Update
                        </StyledButton>
                    </StyledButtonGroup>
                </>
            }
            onCancel={onCancel}
            {...props}>
            <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                <StyledTable<FieldVariable>
                    dataSource={fields as FieldVariable[]}
                    columns={columns}
                    pagination={false}
                    rowKey="id"
                />
            </form>
        </StyledModal>
    );
};

export default FlowContextModal;
